import { useState, useEffect } from "react";
import { Modal, Form, Select, Button, Typography, Input, Tag, Divider, Space, Row, Col, Popconfirm, Spin, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { get_available_ai_providers } from "../../api/admin.apis";
import AnalyticsSummaryGa4Panel from "./AnalyticsSummaryGa4Panel";
import {
  requiredPromptRule,
  hasStoredPrompt,
  buildPlaygroundSavePayload,
  buildPlaygroundTestPayload,
} from "./aiConfigPayloads";

const ANALYTICS_SUMMARY_FEATURE = "analytics_summary";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Strip markdown code fences (```json ... ```) and try to parse as JSON. */
function parseAiResponse(raw) {
  if (!raw) return { type: "string", value: raw ?? "" };
  const stripped = raw.replace(/^```(?:json)?\s*\n?/m, "").replace(/\n?```\s*$/m, "").trim();
  try {
    return { type: "json", value: JSON.parse(stripped) };
  } catch {
    return { type: "string", value: raw };
  }
}

const PRE_BASE = {
  marginTop: 8, padding: 12, borderRadius: 6,
  whiteSpace: "pre-wrap", wordBreak: "break-word",
  fontSize: 13, lineHeight: 1.6, overflowY: "auto",
};

// ── ResultDisplay ─────────────────────────────────────────────────────────────

const ResultDisplay = ({ result }) => {
  if (!result) return null;

  const { success, ai_response, error_message, metadata, config_used, latency_ms, tokens_used, estimated_cost } = result;
  const parsed = parseAiResponse(ai_response);

  // `summary_source` / `ai_model` are surfaced by feature pipelines that
  // distinguish deterministic vs LLM rendering (analytics_summary today).
  // They may show up either as top-level fields or inside metadata /
  // config_used depending on how the backend echoed the run.
  const summarySource =
    result.summary_source ??
    metadata?.summary_source ??
    config_used?.summary_source;
  const aiModel =
    result.ai_model ??
    metadata?.ai_model ??
    config_used?.ai_model ??
    metadata?.model ??
    config_used?.model;

  const summarySourceColor =
    summarySource === "llm" ? "green"
    : summarySource === "deterministic_renderer" ? "cyan"
    : summarySource === "fallback" ? "red"
    : "default";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* ── Status + stats tags ── */}
      <Space wrap>
        <Tag color={success ? "success" : "error"}>{success ? "✓ Success" : "✗ Failed"}</Tag>
        {latency_ms != null && <Tag color="blue">⏱ {(latency_ms / 1000).toFixed(2)}s</Tag>}
        {tokens_used?.total_tokens != null && (
          <Tag color="purple">
            🪙 {tokens_used.total_tokens.toLocaleString()} tokens
            {" "}
            <Text style={{ fontSize: 11, color: "inherit" }}>
              ({tokens_used.prompt_tokens}↑ + {tokens_used.completion_tokens}↓)
            </Text>
          </Tag>
        )}
        {estimated_cost != null && <Tag color="gold">💰 ${estimated_cost.toFixed(6)}</Tag>}
        {metadata?.provider && <Tag color="orange">{metadata.provider} / {metadata.model}</Tag>}
        {summarySource && (
          <Tag color={summarySourceColor}>
            source: {summarySource}
          </Tag>
        )}
        {aiModel && !metadata?.model && <Tag color="geekblue">ai_model: {aiModel}</Tag>}
      </Space>

      {/* ── Error message ── */}
      {error_message && (
        <div style={{ padding: 10, background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 6 }}>
          <Text type="danger">{error_message}</Text>
        </div>
      )}

      {/* ── AI Response ── */}
      {ai_response && (
        <>
          <Text strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#888" }}>
            AI Response
          </Text>
          {parsed.type === "json" ? (
            <pre style={{ ...PRE_BASE, background: "#f6ffed", border: "1px solid #b7eb8f", flex: 1, margin: 0 }}>
              {JSON.stringify(parsed.value, null, 2)}
            </pre>
          ) : (
            <pre style={{ ...PRE_BASE, background: "#f5f5f5", border: "1px solid #d9d9d9", flex: 1, margin: 0 }}>
              {parsed.value}
            </pre>
          )}
        </>
      )}
    </div>
  );
};

// Language of the TEST input only (which en_content/ar_content row to use and
// the `language` sent to the test endpoint). Prompts are one pair for both.
const TEST_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
];

const formatLabel = (name) =>
  name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "-";

const AiPlaygroundModal = ({
  visible,
  config,
  onClose,
  onRunTest,
  testLoading,
  testResult,
  onSaveConfig,
  saveLoading,
  testContentItem,
  testContentLoading,
  onSaveTestContent,
  saveTestContentLoading,
}) => {
  const [form] = Form.useForm();
  const [providers, setProviders] = useState({});
  const [providersLoading, setProvidersLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [currentModelVersion, setCurrentModelVersion] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [ga4ContentLoaded, setGa4ContentLoaded] = useState(false);

  const isAnalyticsSummary = config?.feature_name === ANALYTICS_SUMMARY_FEATURE;

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    setProvidersLoading(true);

    get_available_ai_providers()
      .then((res) => {
        if (cancelled) return;
        setProviders(res?.data?.data?.providers || {});
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Failed to load AI providers");
      })
      .finally(() => {
        if (cancelled) return;
        setProvidersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (visible && config) {
      setSelectedProvider(config.provider || null);
      setCurrentModelVersion(config.model_version || null);
      setSelectedLanguage("en");
      setGa4ContentLoaded(false);
      form.setFieldsValue({
        language: "en",
        provider: config.provider,
        model_version: config.model_version,
        system_prompt: config.system_prompt ?? "",
        user_prompt: config.user_prompt ?? "",
        test_content: isAnalyticsSummary ? "" : (testContentItem?.en_content ?? ""),
      });
    }
  }, [visible, config, form, testContentItem, isAnalyticsSummary]);

  // When the library row resolves after the modal opens, drop its content
  // into the textarea for the currently-selected language without clobbering
  // anything else the admin may have edited.
  useEffect(() => {
    if (!visible || !testContentItem || ga4ContentLoaded || isAnalyticsSummary) return;
    const next =
      selectedLanguage === "ar"
        ? testContentItem.ar_content ?? ""
        : testContentItem.en_content ?? "";
    form.setFieldsValue({ test_content: next });
  }, [visible, testContentItem, selectedLanguage, form, ga4ContentLoaded, isAnalyticsSummary]);

  const handleTestLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    if (!ga4ContentLoaded && !isAnalyticsSummary) {
      form.setFieldsValue({
        test_content: lang === "ar"
          ? (testContentItem?.ar_content ?? "")
          : (testContentItem?.en_content ?? ""),
      });
    }
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    setCurrentModelVersion(null);
    form.setFieldsValue({ model_version: undefined });
  };

  const handleFinish = (values) => {
    onRunTest(buildPlaygroundTestPayload(values, config.feature_name));
  };

  const handleSaveConfig = async () => {
    try {
      await form.validateFields(["provider", "model_version", "system_prompt", "user_prompt"]);
    } catch {
      return; // antd shows inline validation errors
    }
    const systemPrompt = form.getFieldValue("system_prompt");
    const userPrompt = form.getFieldValue("user_prompt");
    const payload = buildPlaygroundSavePayload(
      {
        provider: form.getFieldValue("provider"),
        model_version: form.getFieldValue("model_version"),
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
      },
      config
    );
    onSaveConfig(payload);
  };

  const handleSaveTestContentClick = () => {
    if (!onSaveTestContent) return;
    if (!testContentItem?.id) {
      message.warning("No editable test-content row resolved yet — wait for it to load");
      return;
    }
    const content = form.getFieldValue("test_content") ?? "";
    const payload =
      selectedLanguage === "ar"
        ? { ar_content: content }
        : { en_content: content };
    onSaveTestContent(payload);
  };

  const handleAfterClose = () => {
    form.resetFields();
    setSelectedProvider(null);
    setCurrentModelVersion(null);
    setSelectedLanguage("en");
    setGa4ContentLoaded(false);
  };

  const handleGa4ContentLoaded = (content) => {
    form.setFieldsValue({ test_content: content });
    setGa4ContentLoaded(true);
  };

  const handleGa4ContentCleared = () => {
    setGa4ContentLoaded(false);
    form.setFieldsValue({ test_content: "" });
  };

  const isArabic = selectedLanguage === "ar";

  const providerEntries = Object.entries(providers);
  const providerInfo = selectedProvider ? providers[selectedProvider] : null;
  const fetchedModels = providerInfo?.models || [];
  const modelOptions =
    currentModelVersion &&
    selectedProvider === config?.provider &&
    !fetchedModels.includes(currentModelVersion)
      ? [currentModelVersion, ...fetchedModels]
      : fetchedModels;
  const providerLabel =
    selectedProvider && !providerInfo && !providersLoading
      ? `${selectedProvider} (not available)`
      : null;

  const PANEL_HEIGHT = 600;
  const LABEL_STYLE = { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#888", display: "block", marginBottom: 8 };

  return (
    <Modal
      title={
        <span>
          AI Playground —{" "}
          <Text type="secondary" style={{ fontSize: 14 }}>{formatLabel(config?.feature_name)}</Text>
        </span>
      }
      open={visible}
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      destroyOnClose
      width={1200}
      style={{ top: 20 }}
      styles={{ body: { padding: "16px 24px 24px" } }}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>

        {/* ── Top config bar ── */}
        <Row gutter={12} align="bottom" style={{ marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid #f0f0f0" }}>
          <Col flex="150px">
            <Form.Item label="Test language" name="language" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Select onChange={handleTestLanguageChange}>
                {TEST_LANGUAGE_OPTIONS.map((l) => <Option key={l.value} value={l.value}>{l.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col flex="160px">
            <Form.Item label="Provider" name="provider" rules={[{ required: true, message: "Provider is required" }]} style={{ marginBottom: 0 }}>
              <Select placeholder="Select provider" onChange={handleProviderChange} loading={providersLoading}>
                {providerLabel && (
                  <Option key={selectedProvider} value={selectedProvider}>{providerLabel}</Option>
                )}
                {providerEntries.map(([value, info]) => (
                  <Option key={value} value={value}>{info?.name || value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col flex="220px">
            <Form.Item label="Model Version" name="model_version" rules={[{ required: true, message: "Model version is required" }]} style={{ marginBottom: 0 }}>
              <Select placeholder={selectedProvider ? "Select model" : "Select a provider first"} disabled={!selectedProvider}>
                {modelOptions.map((m) => <Option key={m} value={m}>{m}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col flex="140px">
            <Button type="primary" htmlType="submit" loading={testLoading} block style={{ marginTop: 29 }}>
              ▶&nbsp; Run Test
            </Button>
          </Col>
          <Col flex="150px">
            <Popconfirm
              title="Save configuration?"
              description="This will overwrite the current saved config for this feature."
              okText="Save"
              cancelText="Cancel"
              onConfirm={handleSaveConfig}
            >
              <Button
                icon={<SaveOutlined />}
                loading={saveLoading}
                block
                style={{ marginTop: 29 }}
              >
                Save Config
              </Button>
            </Popconfirm>
          </Col>
        </Row>

        {isAnalyticsSummary && (
          <AnalyticsSummaryGa4Panel
            visible={visible}
            language={selectedLanguage}
            ga4ContentLoaded={ga4ContentLoaded}
            onContentLoaded={handleGa4ContentLoaded}
            onContentCleared={handleGa4ContentCleared}
          />
        )}

        {/* ── Left / Right split ── */}
        <Row gutter={0} style={{ minHeight: PANEL_HEIGHT }}>

          {/* ── LEFT: inputs ── */}
          <Col span={12} style={{ paddingRight: 16, borderRight: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 4 }}>
            <Text style={LABEL_STYLE}>Input</Text>

            {!hasStoredPrompt(config) && (
              <Text type="warning" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                No stored prompt: the code default is used until you save one
              </Text>
            )}

            <Form.Item
              label="System Prompt" name="system_prompt" style={{ marginBottom: 8 }}
              rules={[requiredPromptRule("System prompt")]}
              extra={<><strong>Note:</strong> Must wrap with <code>"""</code> at start and end. Maintain example structure. One prompt serves English and Arabic; the backend adds a <code>LANGUAGE: en|ar</code> line to the user message.</>}
            >
              <TextArea style={{ minHeight: 150, resize: "vertical" }} placeholder="System prompt..." dir="auto" />
            </Form.Item>

            <Form.Item
              label="User Prompt" name="user_prompt" style={{ marginBottom: 8 }}
              rules={[requiredPromptRule("User prompt")]}
              extra={<><strong>Note:</strong> Must wrap with <code>"""</code> at start and end. Maintain example structure. The backend adds a <code>LANGUAGE: en|ar</code> line (from Test language) at the top of this message.</>}
            >
              <TextArea style={{ minHeight: 150, resize: "vertical" }} placeholder="User prompt..." dir="auto" />
            </Form.Item>

            <Divider style={{ margin: "8px 0" }} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <Space size={6} wrap>
                <Text strong style={{ fontSize: 14 }}>Test Content</Text>
                {ga4ContentLoaded ? (
                  <Tag color="green" style={{ marginRight: 0 }}>GA4</Tag>
                ) : testContentLoading ? (
                  <Spin size="small" />
                ) : testContentItem?.id ? (
                  <>
                    <Tag color="cyan" style={{ marginRight: 0 }}>library</Tag>
                    {testContentItem.is_default && <Tag color="blue" style={{ marginRight: 0 }}>default</Tag>}
                  </>
                ) : (
                  <Tag color="default" style={{ marginRight: 0 }}>unsaved</Tag>
                )}
              </Space>
              <Popconfirm
                title="Save test content?"
                description={`This overwrites the ${selectedLanguage === "ar" ? "Arabic" : "English"} content on the library row for "${config?.feature_name}".`}
                okText="Save"
                cancelText="Cancel"
                onConfirm={handleSaveTestContentClick}
                disabled={!testContentItem?.id || ga4ContentLoaded}
              >
                <Button
                  size="small"
                  icon={<SaveOutlined />}
                  loading={saveTestContentLoading}
                  disabled={!testContentItem?.id || ga4ContentLoaded}
                >
                  Save Test Content
                </Button>
              </Popconfirm>
            </div>

            {isAnalyticsSummary && !ga4ContentLoaded && (
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                Use <strong>Load from GA4</strong> above to populate test content from live analytics data.
              </Text>
            )}

            <Form.Item
              name="test_content"
              rules={[{ required: true, message: "Test content is required" }]}
              style={{ marginBottom: 0 }}
            >
              <TextArea
                style={{ minHeight: 140, resize: "vertical" }}
                placeholder={
                  ga4ContentLoaded
                    ? "Test content loaded from GA4 (read-only)"
                    : isAnalyticsSummary
                      ? "Load from GA4 above, or paste sample content manually..."
                      : testContentLoading
                        ? "Loading from library..."
                        : "Paste sample content here to test against the prompts..."
                }
                dir={isArabic ? "rtl" : undefined}
                disabled={ga4ContentLoaded}
                readOnly={ga4ContentLoaded}
              />
            </Form.Item>
          </Col>

          {/* ── RIGHT: output ── */}
          <Col span={12} style={{ paddingLeft: 16, display: "flex", flexDirection: "column" }}>
            <Text style={LABEL_STYLE}>Output</Text>
            {testResult ? (
              <ResultDisplay result={testResult} />
            ) : (
              <div style={{
                flex: 1, minHeight: PANEL_HEIGHT - 24,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                background: "#fafafa", borderRadius: 8, border: "1px dashed #d9d9d9", gap: 8,
              }}>
                <Text style={{ fontSize: 24 }}>🧪</Text>
                <Text type="secondary" style={{ fontSize: 13 }}>Output will appear here after running the test</Text>
              </div>
            )}
          </Col>

        </Row>
      </Form>
    </Modal>
  );
};

export default AiPlaygroundModal;

